import { Feature, Geometry } from 'geojson';
import { FloodingListItem } from 'features/flooding/flooding.types';
import {
  FLOOD_ICON_TO_SELECTED_NAME,
  getFloodingEventIcon,
  getFloodingEventScale,
  getOpacityForGeoEventMarker,
} from 'features/mapping/icons';
import { NotificationType } from 'features/notifications/notifications.constants';
import { MapClickHandler, MapboxFeature } from 'features/mapping/mapping.types';
import { history } from 'shared/history';
import { ONE_YEAR_MS } from './FloodingsLayer.constants';

export type FloodingsLayerProps = {
  floodingEvents: FloodingListItem[];
  selectedGeoEventId?: number;
  interactive?: boolean;
  isFadable?: boolean;
  emphasizedGeoEventIds?: Set<number>;
  isEdit?: boolean;
};

export type FloodingFeatureProperties = {
  id: number;
  name: string;
  icon: string;
  selectedIcon: string;
  isActive: boolean;
  scale: number;
  offsetX: number;
  offsetY: number;
  opacity: number;
  index: number;
  indexInv: number;
  geoEventId: number;
};

const getSortIndex = (floodingEvent: FloodingListItem): number => {
  const {
    isActive,
    notificationType,
    dateCreated,
    data: { isParent },
  } = floodingEvent;

  if (!isActive) {
    return 0;
  }

  if (notificationType === NotificationType.SILENT) {
    return 1;
  }

  const defaultSortIndex = dateCreated.getTime();

  if (isParent) {
    return defaultSortIndex + ONE_YEAR_MS;
  }

  return defaultSortIndex;
};

export const toGeojsonFeature = (
  floodingEvent: FloodingListItem,
  isFadable: boolean,
  emphasizedGeoEventIds?: Set<number>,
): Feature<Geometry, FloodingFeatureProperties> => {
  const { isActive, id, name, lat, lng } = floodingEvent;
  const icon = getFloodingEventIcon(floodingEvent);

  // If there are active filters, and this incident is not in the matching set,
  // we reduce the size and opacity of the icon.
  const isFilteredOut = emphasizedGeoEventIds && !emphasizedGeoEventIds.has(id);
  const opacity = getOpacityForGeoEventMarker({
    geoEvent: floodingEvent,
    isFadable,
    isFilteredOut,
  });

  // Sort indexes for labels and text. Inactive on the bottom,
  // then oldest to newest.
  const index = getSortIndex(floodingEvent);
  const indexInv = 1 / (index + 1);

  const geoEventScale = getFloodingEventScale(floodingEvent, isFilteredOut);

  // Calculate scale and associated offset.
  const iconScale = icon.scale || 1.0;
  const scale = geoEventScale * iconScale;
  const offsetX = icon.offset ? icon.offset[0] * scale : 0;
  const offsetY = icon.offset ? icon.offset[1] * scale : 0;

  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [lng, lat],
    },
    properties: {
      id,
      name,
      icon: icon.name,
      selectedIcon: FLOOD_ICON_TO_SELECTED_NAME[icon.name] || icon.name,
      isActive,
      scale,
      offsetX,
      offsetY,
      opacity,
      index,
      indexInv,
      geoEventId: id,
    },
  };
};

export const resolveFloodingPath = (
  geoJsonFeatures: MapboxFeature<FloodingFeatureProperties>[],
): string | null => {
  const feature = geoJsonFeatures[0];
  const { id, opacity } = feature.properties;
  if (opacity > 0) return `/i/${id}`;
  return null;
};

export const floodingsClickHandler: MapClickHandler<
  FloodingFeatureProperties
> = (geoJsonFeatures) => {
  const path = resolveFloodingPath(geoJsonFeatures);
  if (path) history.push(path);
};
